'use client'

import { SafeImage } from '@/components/ui/safe-image'
import { cn } from '@/lib/utils'
import { Package } from '@phosphor-icons/react'
import { useState } from 'react'

interface PhotoThumbProps {
  src: string | null
  alt: string
  /** Rendered size hint for next/image, e.g. "56px" or "40vw". */
  sizes: string
  className?: string
}

/**
 * Item or part photo on a Warm Fog backing tile. Multiply blending dissolves the white
 * backgrounds of catalog product shots into the tile, so they sit alongside
 * phone photos as one set. Dark mode skips the blend (it would crush photos).
 * A photo that fails to load falls back to the same placeholder as no photo.
 */
export function PhotoThumb({ src, alt, sizes, className }: PhotoThumbProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const showImage = !!src && failedSrc !== src

  return (
    // isolate: keep mix-blend-multiply contained to this tile (blending against
    // its own bg-muted, the intended look). Without it the blend reaches the page
    // backdrop, and that cross-boundary blend layer blocks the horizontal shelf
    // from scrolling when a swipe starts on a tile (Android Chrome, light mode).
    <div className={cn('relative isolate shrink-0 overflow-hidden bg-muted', className)}>
      {showImage ? (
        <SafeImage
          src={src}
          alt={alt}
          fill
          sizes={sizes}
          onError={() => setFailedSrc(src)}
          className="pointer-events-none object-cover mix-blend-multiply dark:mix-blend-normal"
        />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground/60">
          <Package className="size-1/3 max-h-8 max-w-8" aria-hidden />
        </div>
      )}
    </div>
  )
}
